import { account, tablesDB, DATABASE_ID, USERS_TABLE_ID } from '../lib/appwrite';

// Simple in-memory cache to avoid repeated `account.get()` and `listSessions()` calls
let _cachedUser = null;
let _cachedUserAt = 0;
let _cachedSessions = null;
let _cachedSessionsAt = 0;
const CACHE_TTL = 30 * 1000; // 30s

const isCacheValid = (ts) => ts && (Date.now() - ts) < CACHE_TTL;

class AuthService {
  /**
   * Get current user session
   */
  static async getCurrentUser() {
    try {
      if (isCacheValid(_cachedUserAt) && _cachedUser) return _cachedUser;
      const user = await account.get();
      _cachedUser = user;
      _cachedUserAt = Date.now();
      return user;
    } catch (error) {
      throw new Error('No user is currently signed in');
    }
  }

  /**
   * Get current user's ID
   */
  static async getCurrentUserId() {
    try {
      const user = await this.getCurrentUser();
      return user.$id;
    } catch (error) {
      throw new Error('No user is currently signed in');
    }
  }

  /**
   * Get current user's email
   */
  static async getCurrentUserEmail() {
    try {
      const user = await this.getCurrentUser();
      return user.email;
    } catch (error) {
      throw new Error('No user is currently signed in');
    }
  }

  /**
   * Check if user is authenticated and has valid session
   * Also verifies user is not a buyer
   */
  static async isAuthenticated() {
    try {
      // Use cached user/sessions when valid to reduce network calls
      const user = await this.getCurrentUser();

      const sessions = isCacheValid(_cachedSessionsAt) && _cachedSessions
        ? _cachedSessions
        : await account.listSessions();
      if (!isCacheValid(_cachedSessionsAt)) {
        _cachedSessions = sessions;
        _cachedSessionsAt = Date.now();
      }
      const hasActiveSession = sessions && sessions.sessions && sessions.sessions.length > 0;
      
      if (!hasActiveSession) {
        // console.warn('⚠️ User exists but no active session found');
        return false;
      }
      
      // Check if user is a buyer (block buyers from seller app)
      try {
        const userRow = await tablesDB.getRow(DATABASE_ID, USERS_TABLE_ID, user.$id);
        const userRole = (userRow.role || userRow.roles || '').toLowerCase().trim();
        
        if (userRole === 'buyer') {
          console.error('🚫 BLOCKED: Buyer detected with active session - blocking access');
          // Delete all sessions for this buyer
          await this.deleteAllSessions();
          return false;
        }
      } catch (roleError) {
        // If we can't check role, allow authentication (best effort)
        // User might not exist in users table yet
        // console.warn('⚠️ Could not verify user role during auth check:', roleError.message);
      }
      
      // console.log('✅ Valid session verified for user:', user.$id);
      return true;
    } catch (error) {
      // console.log('❌ Authentication check failed:', error.type || error.message);
      return false;
    }
  }

  /**
   * Verify session is still valid (more thorough check)
   * Also verifies user is not a buyer
   */
  static async verifySession() {
    try {
      // Use cached user/sessions when available to speed up checks
      const user = isCacheValid(_cachedUserAt) && _cachedUser ? _cachedUser : await account.get();
      if (!isCacheValid(_cachedUserAt)) {
        _cachedUser = user;
        _cachedUserAt = Date.now();
      }

      const sessions = isCacheValid(_cachedSessionsAt) && _cachedSessions ? _cachedSessions : await account.listSessions();
      if (!isCacheValid(_cachedSessionsAt)) {
        _cachedSessions = sessions;
        _cachedSessionsAt = Date.now();
      }

      if (!sessions || !sessions.sessions || sessions.sessions.length === 0) {
        throw new Error('No active session');
      }
      
      // Check if user is a buyer (block buyers from seller app)
      try {
        const userRow = await tablesDB.getRow(DATABASE_ID, USERS_TABLE_ID, user.$id);
        const userRole = (userRow.role || userRow.roles || '').toLowerCase().trim();
        
        if (userRole === 'buyer') {
          console.error('🚫 BLOCKED: Buyer detected with active session - blocking access');
          // Delete all sessions for this buyer
          await this.deleteAllSessions();
          throw new Error('This app is only for sellers. Please use the buyer app to access your account.');
        }
      } catch (roleError) {
        // If it's a buyer-specific error, re-throw it
        if (roleError.message && roleError.message.includes('Buyers cannot access')) {
          throw roleError;
        }
        // If we can't check role, allow session (best effort)
        // User might not exist in users table yet
        // console.warn('⚠️ Could not verify user role during session verification:', roleError.message);
      }
      
      return {
        valid: true,
        user: user,
        session: sessions.sessions[0]
      };
    } catch (error) {
      return {
        valid: false,
        error: error.message || 'Session invalid'
      };
    }
  }

  /**
   * Create email session (login)
   * Blocks buyers from logging into the seller app
   */
  static async createEmailSession(email, password) {
    try {
      const session = await account.createEmailPasswordSession(email, password);
      // console.log('✅ Login successful, session created:', session.$id);
      
      // Check user role to prevent buyers from accessing seller app
      // This is REQUIRED - we must verify the user is not a buyer
      const userId = session.userId;
      let userRow;
      try {
        userRow = await tablesDB.getRow(DATABASE_ID, USERS_TABLE_ID, userId);
      } catch (getRowError) {
        // If user doesn't exist in users table, that's okay for new sellers
        // They'll be created during store registration
        // console.warn('⚠️ User not found in users table yet:', getRowError.message);
        // Allow login to continue - user will be created during store registration
        return session;
      }
      
      // User exists in users table - check their role
      const userRole = (userRow.role || userRow.roles || '').toLowerCase().trim();
      // console.log('🔍 Checking user role:', userRole, 'for user:', userId);
      
      // Block buyers from accessing seller app (case-insensitive check)
      if (userRole === 'buyer') {
        console.error('🚫 BLOCKED: Buyer attempted to login to seller app');
        // Delete the session immediately
        try {
          await account.deleteSession(session.$id);
        } catch (deleteError) {
          console.error('Error deleting buyer session:', deleteError);
        }
        throw new Error('This app is only for sellers. Please use the buyer app to access your account.');
      }
      
      // Allow sellers and other roles (ShopOwner, Farmer, Driver, Admin, seller)
      // console.log('✅ User role verified - allowed:', userRole);
      
      return session;
    } catch (error) {
      console.error('❌ Login error:', {
        type: error.type,
        code: error.code,
        message: error.message,
        response: error.response
      });
      // If it's our buyer blocking error, throw it as-is
      if (error.message && error.message.includes('Buyers cannot access')) {
        throw error;
      }
      throw new Error(this.getErrorMessage(error));
    }
  }

  /**
   * Create phone session (login)
   * Blocks buyers from logging into the seller app
   */
  static async createPhoneSession(phone, password) {
    try {
      const session = await account.createPhonePasswordSession(phone, password);
      
      // Check user role to prevent buyers from accessing seller app
      // This is REQUIRED - we must verify the user is not a buyer
      const userId = session.userId;
      let userRow;
      try {
        userRow = await tablesDB.getRow(DATABASE_ID, USERS_TABLE_ID, userId);
      } catch (getRowError) {
        // If user doesn't exist in users table, that's okay for new sellers
        // They'll be created during store registration
        // console.warn('⚠️ User not found in users table yet:', getRowError.message);
        // Allow login to continue - user will be created during store registration
        return session;
      }
      
      // User exists in users table - check their role
      const userRole = (userRow.role || userRow.roles || '').toLowerCase().trim();
      // console.log('🔍 Checking user role:', userRole, 'for user:', userId);
      
      // Block buyers from accessing seller app (case-insensitive check)
      if (userRole === 'buyer') {
        console.error('🚫 BLOCKED: Buyer attempted to login to seller app');
        // Delete the session immediately
        try {
          await account.deleteSession(session.$id);
        } catch (deleteError) {
          console.error('Error deleting buyer session:', deleteError);
        }
        throw new Error('This app is only for sellers. Please use the buyer app to access your account.');
      }
      
      // Allow sellers and other roles (ShopOwner, Farmer, Driver, Admin, seller)
      // console.log('✅ User role verified - allowed:', userRole);
      
      return session;
    } catch (error) {
      // If it's our buyer blocking error, throw it as-is
      if (error.message && error.message.includes('Buyers cannot access')) {
        throw error;
      }
      throw new Error(this.getErrorMessage(error));
    }
  }

  static clearAuthCache() {
    _cachedUser = null;
    _cachedUserAt = 0;
    _cachedSessions = null;
    _cachedSessionsAt = 0;
  }

  /**
   * End any existing browser session and clear cached auth before account.create().
   * Prevents guest / stale cookies from causing 401 or 500 during email signup.
   */
  static async clearClientBeforeSignup() {
    this.clearAuthCache();
    try {
      await account.deleteSessions();
      return;
    } catch (_) {
      /* no session or guest */
    }
    try {
      await account.deleteSession('current');
    } catch (_) {
      /* ignore */
    }
  }

  /**
   * Delete current session (logout)
   */
  static async deleteSession(sessionId = 'current') {
    try {
      await account.deleteSession(sessionId);
    } catch (error) {
      throw new Error('Failed to logout');
    }
  }

  /**
   * Delete all sessions (logout from all devices)
   */
  static async deleteAllSessions() {
    try {
      const sessions = await account.listSessions();
      const deletePromises = sessions.sessions.map(session => 
        account.deleteSession(session.$id)
      );
      await Promise.all(deletePromises);
      this.clearAuthCache();
    } catch (error) {
      throw new Error('Failed to logout from all devices');
    }
  }

  /**
   * Emails the account a link to /reset-password with the recovery
   * userId + secret Appwrite appends as query params.
   */
  static async sendPasswordRecovery(email) {
    try {
      const url = `${window.location.origin}/reset-password`;
      await account.createRecovery(email, url);
    } catch (error) {
      throw new Error(this.getErrorMessage(error));
    }
  }

  /**
   * Completes a recovery started by [sendPasswordRecovery], using the
   * userId + secret from the link's query params.
   */
  static async confirmPasswordRecovery(userId, secret, password) {
    try {
      await account.updateRecovery(userId, secret, password);
    } catch (error) {
      throw new Error(this.getErrorMessage(error));
    }
  }

  /**
   * Completes the email verification Appwrite sends on signup, using the
   * userId + secret from the link's query params (mobile signup sends
   * this; the mobile app itself can't complete it, since the link opens
   * in a browser, not the app).
   */
  static async confirmEmailVerification(userId, secret) {
    try {
      await account.updateVerification(userId, secret);
    } catch (error) {
      throw new Error(this.getErrorMessage(error));
    }
  }

  /**
   * Get error message from Appwrite error
   */
  static getErrorMessage(error) {
    // Check error type first
    if (error.type === 'user_invalid_credentials') {
      return 'Invalid email or password. Please try again.';
    }
    if (error.type === 'user_not_found') {
      return 'No user found with this email. Please register first.';
    }
    if (error.type === 'user_invalid_token') {
      return 'This link has expired or was already used. Please request a new one.';
    }
    if (error.type === 'user_session_already_exists') {
      return 'You are already logged in.';
    }
    if (error.type === 'user_email_already_exists') {
      return 'An account with this email already exists.';
    }
    if (error.type === 'user_phone_already_exists') {
      return 'An account with this phone number already exists.';
    }
    if (error.type === 'general_rate_limit_exceeded') {
      return 'Too many requests. Please try again later.';
    }
    if (error.type === 'user_unauthorized') {
      return 'You are not authorized. Please check your credentials.';
    }
    if (error.code === 401) {
      return 'Invalid email or password. Please check your credentials and try again.';
    }
    
    // Check error message
    const errorMsg = error.message || '';
    if (errorMsg.includes('Invalid credentials') || errorMsg.includes('invalid')) {
      return 'Invalid email or password. Please try again.';
    }
    if (errorMsg.includes('not found')) {
      return 'No account found with this email. Please register first.';
    }
    
    return errorMsg || 'Login failed. Please check your credentials and try again.';
  }
}

export default AuthService;
